// frontend/src/pages/ForgotPassword.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Mail, User, ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';
import { authAPI } from '../services/api';
import weplLogo from '../assets/images/wepl_logo.png';

const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const STAFF_ID_PATTERN = /^[A-Za-z0-9-]{2,20}$/;

const ForgotPassword = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedIdentifier, setSubmittedIdentifier] = useState('');
  const [submittedKind, setSubmittedKind] = useState('email');
  const [error, setError] = useState(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const identifierValue = watch('identifier', '');
  const typingEmail = (identifierValue || '').includes('@');

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setError(null);

    const raw = data.identifier.trim();
    const isEmail = raw.includes('@');
    const payload = isEmail
      ? { email: raw.toLowerCase() }
      : { staff_id: raw.toUpperCase() };

    try {
      await authAPI.forgotPassword(payload);
    } catch (err) {
      // Show success regardless to prevent account enumeration.
    } finally {
      setSubmittedIdentifier(isEmail ? raw.toLowerCase() : raw.toUpperCase());
      setSubmittedKind(isEmail ? 'email' : 'staffId');
      setIsSubmitted(true);
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    document.body.classList.add('no-scroll');
    return () => document.body.classList.remove('no-scroll');
  }, []);

  const maskEmail = (email) => {
    if (!email) return '';
    const [local, domain] = email.split('@');
    if (!domain) return email;
    const maskedLocal =
      local.length > 2
        ? local[0] + '*'.repeat(Math.min(local.length - 2, 5)) + local[local.length - 1]
        : local;
    return `${maskedLocal}@${domain}`;
  };

  const maskStaffId = (staffId) => {
    if (!staffId || staffId.length < 4) return staffId;
    return `${staffId.slice(0, 2)}${'*'.repeat(staffId.length - 4)}${staffId.slice(-2)}`;
  };

  const maskedIdentifier = submittedKind === 'email'
    ? maskEmail(submittedIdentifier)
    : maskStaffId(submittedIdentifier);

  return (
    <div className="h-dvh overflow-auto overscroll-contain bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center px-4 py-6">
      <div className="w-full max-w-sm space-y-5">

        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center">
            <div className="w-20 h-20 flex items-center justify-center overflow-hidden">
              <img
                src={weplLogo}
                alt="WEPL Logo"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
          <h1 className="mt-3 text-2xl font-bold text-gray-900">
            {isSubmitted ? 'Check Your Email' : 'Forgot Password'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {isSubmitted
              ? "We've sent you instructions to reset your password."
              : "Enter your staff ID or work email and we'll send a reset link."}
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-xl shadow-soft px-6 py-7">
          {isSubmitted ? (
            <div className="text-center">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-7 h-7 text-green-600" />
              </div>

              <p className="text-sm text-gray-600 mb-1">If an account exists for</p>
              <p className="font-semibold text-gray-900 mb-3">{maskedIdentifier}</p>
              <p className="text-sm text-gray-500 mb-5">
                {submittedKind === 'staffId'
                  ? 'a reset link has been sent to the email address on file.'
                  : 'you will receive an email with instructions to reset your password.'}
              </p>

              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-5 text-left">
                <p className="text-sm text-amber-800">
                  <strong>Didn't receive it?</strong>{' '}
                  {submittedKind === 'staffId'
                    ? 'Check that inbox, including spam. Workmen without a company email should check the personal email on their profile.'
                    : 'Check your spam folder or verify the email address is correct.'}
                </p>
              </div>

              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 text-sm text-blue-800 hover:text-blue-700 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </Link>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
              <div>
                <label htmlFor="identifier" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Staff ID or Work Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    {typingEmail ? (
                      <Mail className="h-4 w-4 text-gray-400" />
                    ) : (
                      <User className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                  <input
                    id="identifier"
                    {...register('identifier', {
                      required: 'Staff ID or work email is required',
                      validate: (value) => {
                        const trimmed = (value || '').trim();
                        if (!trimmed) return 'Staff ID or work email is required';
                        if (trimmed.includes('@')) {
                          return EMAIL_PATTERN.test(trimmed) || 'Please enter a valid email address';
                        }
                        return STAFF_ID_PATTERN.test(trimmed)
                          || 'Enter your staff ID, for example W0123, or your work email';
                      },
                    })}
                    type="text"
                    className={`input pl-9 ${errors.identifier ? 'input-error' : ''}`}
                    placeholder="Enter your staff ID or work email"
                    autoComplete="username"
                    aria-describedby={
                      errors.identifier ? 'identifier-error identifier-help' : 'identifier-help'
                    }
                  />
                </div>
                {errors.identifier ? (
                  <p id="identifier-error" className="mt-1 text-xs text-error-600">
                    {errors.identifier.message}
                  </p>
                ) : null}
                <p id="identifier-help" className="mt-1.5 text-xs text-gray-500">
                  No company email? Enter your Staff ID. The link goes to the personal email on your profile.
                </p>
              </div>

              {error && (
                <div
                  role="alert"
                  aria-live="assertive"
                  className="bg-error-50 border border-error-200 rounded-lg px-3 py-2.5"
                >
                  <p className="text-sm text-error-600">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn btn-primary w-full h-11 text-sm"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Sending…</span>
                  </div>
                ) : (
                  'Send Reset Link'
                )}
              </button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </Link>
              </div>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-gray-400">
          Need help? Contact your HR administrator.
        </p>

      </div>
    </div>
  );
};

export default ForgotPassword;
